// FILE: src/pages/HR/HREmployeeManagement.js
//
// WHAT CHANGED vs YOUR CURRENT FILE:
// ─────────────────────────────────────────────────────────────────
// ORIGINAL: Create Branch section had only a name field.
//
// NEW: Added "Branch GPS Locations" section (between Create Branch and Employee Form)
//   - Shows a table of all branches
//   - Each row has: Branch Name | Latitude | Longitude | Radius (metres) | Save button
//   - HR/Owner types the GPS coordinates from Google Maps and clicks Save
//   - Calls PUT /api/branches/{id}/location with { latitude, longitude, radiusMeters }
//   - After save, employees clocking in outside the radius are blocked with:
//     "You are Xm away from [Branch]. You must be within Ym to clock in."
//   - Leaving lat/lng blank clears geofencing for that branch (graceful opt-out)
//
// All other code (employee form, employee table, change-password dialog) UNCHANGED.
// ─────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, TextField, Button,
    Select, MenuItem, FormControl, InputLabel,
    Table, TableHead, TableRow, TableCell, TableBody,
    Checkbox, ListItemText, Dialog, DialogTitle,
    DialogContent, DialogActions, Chip
} from "@mui/material";
import api from "../../api/axios";
import toast from "react-hot-toast";

export default function HREmployeeManagement() {

    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [managers, setManagers] = useState([]);
    const [approvalManagers, setApprovalManagers] = useState([]);
    const [branchName, setBranchName] = useState("");

    // NEW: tracks unsaved edits to branch GPS fields before "Save" is clicked
    // shape: { [branchId]: { latitude, longitude, radiusMeters } }
    const [branchLocationEdits, setBranchLocationEdits] = useState({});

    // Change password dialog
    const [pwDialog, setPwDialog] = useState(false);
    const [pwTarget, setPwTarget] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [pwLoading, setPwLoading] = useState(false);

    const [form, setForm] = useState({
        id: null,
        name: "",
        email: "",
        basicSalary: "",
        branchId: "",
        managerId: "",
        approvalManagerId: "",
        role: "EMPLOYEE",
        designation: "",
        employeeCode: "",
        department: "",
        payDay: "",
        payrollCycleType: "",
        endOfMonthPayroll: false,
        managedBranches: []
    });

    // ── LOAD DATA ──
    const loadData = async () => {
        try {
            const [b, e, m, am] = await Promise.all([
                api.get("/branches"),
                api.get("/employees"),
                api.get("/employees/managers"),
                api.get("/employees/approval-managers").catch(() => ({ data: [] }))
            ]);
            setBranches(b.data || []);
            setEmployees(e.data || []);
            setManagers(m.data || []);
            setApprovalManagers(am.data || []);
        } catch (err) {
            console.error("loadData error:", err);
            toast.error("Failed to load data");
        }
    };

    useEffect(() => { loadData(); }, []);

    // ── CREATE BRANCH ──
    const createBranch = async () => {
        if (!branchName.trim()) return toast.error("Enter branch name");
        try {
            await api.post("/branches", { name: branchName });
            setBranchName("");
            loadData();
            toast.success("Branch created");
        } catch (err) {
            toast.error("Branch creation failed: " + (err?.response?.data?.message || err.message));
        }
    };

    // ── NEW: track edits to a branch's GPS fields ──
    const handleLocationFieldChange = (branchId, field, value) => {
        setBranchLocationEdits(prev => ({
            ...prev,
            [branchId]: { ...prev[branchId], [field]: value }
        }));
    };

    // ── NEW: save GPS location for a branch ──
    const saveBranchLocation = async (branch) => {
        const edit = branchLocationEdits[branch.id] || {};
        const latitude = edit.latitude !== undefined ? edit.latitude : (branch.latitude ?? '');
        const longitude = edit.longitude !== undefined ? edit.longitude : (branch.longitude ?? '');
        const radiusMeters = edit.radiusMeters !== undefined ? edit.radiusMeters : (branch.radiusMeters ?? 200);

        try {
            await api.put(`/branches/${branch.id}/location`, {
                latitude: latitude !== '' ? Number(latitude) : null,
                longitude: longitude !== '' ? Number(longitude) : null,
                radiusMeters: radiusMeters !== '' ? Number(radiusMeters) : 200
            });
            toast.success(`GPS location saved for ${branch.name}`);
            // Clear the unsaved edits for this branch now that they're saved
            setBranchLocationEdits(prev => {
                const next = { ...prev };
                delete next[branch.id];
                return next;
            });
            loadData();
        } catch (err) {
            toast.error("Failed to save: " + (err?.response?.data?.message || err.message));
        }
    };

    // ── HANDLE CHANGE ──
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    // ── CREATE / UPDATE ──
    const createEmployee = async () => {
        const payload = {
            name: form.name,
            email: form.email,
            basicSalary: Number(form.basicSalary),
            branchId: Number(form.branchId),
            managerId: form.managerId ? Number(form.managerId) : null,
            approvalManagerId: form.approvalManagerId ? Number(form.approvalManagerId) : null,
            role: form.role,
            designation: form.designation,
            employeeCode: form.employeeCode || null,
            department: form.department || null,
            payDay: form.payDay ? Number(form.payDay) : null,
            payrollCycleType: form.payrollCycleType,
            endOfMonthPayroll: form.endOfMonthPayroll || false,
            managedBranchIds: form.managedBranches
        };
        try {
            if (form.id) {
                await api.put(`/employees/${form.id}`, payload);
                toast.success("Employee updated");
            } else {
                await api.post("/employees", payload);
                toast.success("Employee created successfully");
            }
            resetForm();
            loadData();
        } catch (err) {
            toast.error("Save failed: " + (err?.response?.data?.message || err.message));
        }
    };

    // ── DELETE ──
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete employee "${name}"? This will also delete their user account and all linked data.`)) return;
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee deleted");
            loadData();
        } catch (err) {
            toast.error("Delete failed: " + (err?.response?.data?.message || err.message));
        }
    };

    // ── EDIT ──
    const handleEdit = (emp) => {
        setForm({
            id: emp.id,
            name: emp.name || "",
            email: emp.email || "",
            basicSalary: emp.basicSalary || "",
            branchId: emp.branchId || "",
            managerId: emp.managerId || "",
            approvalManagerId: emp.approvalManagerId || "",
            role: emp.role || "EMPLOYEE",
            designation: emp.designation || "",
            employeeCode: emp.employeeCode || "",
            department: emp.department || "",
            payDay: emp.payDay || "",
            payrollCycleType: emp.payrollCycleType || "",
            endOfMonthPayroll: emp.endOfMonthPayroll || false,
            managedBranches: emp.managedBranchIds || []
        });
    };

    // ── RESET ──
    const resetForm = () => {
        setForm({
            id: null, name: "", email: "", basicSalary: "",
            branchId: "", managerId: "", approvalManagerId: "",
            role: "EMPLOYEE", designation: "", employeeCode: "",
            department: "", payDay: "", payrollCycleType: "",
            endOfMonthPayroll: false, managedBranches: []
        });
    };

    // ── CHANGE PASSWORD ──
    const openPwDialog = (emp) => { setPwTarget(emp); setNewPassword(""); setPwDialog(true); };
    const closePwDialog = () => { setPwDialog(false); setPwTarget(null); setNewPassword(""); };

    const submitChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        if (!pwTarget) return;
        setPwLoading(true);
        try {
            await api.post("/auth/hr-reset-password", {
                username: pwTarget.username || pwTarget.email,
                newPassword: newPassword
            });
            toast.success(`Password changed for ${pwTarget.name}`);
            closePwDialog();
        } catch (err) {
            toast.error("Failed: " + (err?.response?.data?.message || err.message));
        } finally {
            setPwLoading(false);
        }
    };

    /* ─────────────────────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────────────────────── */
    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight={800} mb={1}>HR Management Panel</Typography>

            {/* ── CREATE BRANCH (unchanged) ── */}
            <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={1.5}>Create Branch</Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <TextField
                        label="Branch Name"
                        value={branchName}
                        onChange={(e) => setBranchName(e.target.value)}
                        size="small"
                        sx={{ minWidth: 260 }}
                    />
                    <Button variant="contained" onClick={createBranch}>
                        Create Branch
                    </Button>
                </Box>
            </Paper>

            {/* ── NEW: BRANCH GPS LOCATIONS ── */}
            <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={0.5}>
                    Branch GPS Locations (Geofencing)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Set GPS coordinates for each branch. Employees must be within the radius to clock in/out.
                    Get coordinates from Google Maps (right-click on your office → Copy coordinates).
                    Leave latitude/longitude blank to disable geofencing for that branch.
                </Typography>

                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                                <TableCell><b>Branch</b></TableCell>
                                <TableCell><b>Latitude</b></TableCell>
                                <TableCell><b>Longitude</b></TableCell>
                                <TableCell><b>Radius (metres)</b></TableCell>
                                <TableCell><b>Action</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branches.map(b => {
                                const edit = branchLocationEdits[b.id] || {};
                                const latVal = edit.latitude !== undefined ? edit.latitude : (b.latitude ?? '');
                                const lngVal = edit.longitude !== undefined ? edit.longitude : (b.longitude ?? '');
                                const radVal = edit.radiusMeters !== undefined ? edit.radiusMeters : (b.radiusMeters ?? 200);
                                return (
                                    <TableRow key={b.id}>
                                        <TableCell><b>{b.name}</b></TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small" type="number"
                                                inputProps={{ step: 'any' }}
                                                placeholder="e.g. 13.6288"
                                                value={latVal}
                                                onChange={e => handleLocationFieldChange(b.id, 'latitude', e.target.value)}
                                                sx={{ width: 150 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small" type="number"
                                                inputProps={{ step: 'any' }}
                                                placeholder="e.g. 79.4192"
                                                value={lngVal}
                                                onChange={e => handleLocationFieldChange(b.id, 'longitude', e.target.value)}
                                                sx={{ width: 150 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small" type="number"
                                                value={radVal}
                                                onChange={e => handleLocationFieldChange(b.id, 'radiusMeters', e.target.value)}
                                                sx={{ width: 100 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small" variant="contained"
                                                onClick={() => saveBranchLocation(b)}
                                            >
                                                Save
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {branches.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                                        No branches yet. Create one above first.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* ── EMPLOYEE FORM (unchanged) ── */}
            <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                    {form.id ? "Update Employee" : "Create Employee"}
                </Typography>

                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(3,1fr)" }}>

                    <TextField name="name" label="Name" value={form.name} onChange={handleChange} />
                    <TextField name="email" label="Email" value={form.email} onChange={handleChange} />
                    <TextField name="basicSalary" label="Salary" type="number" value={form.basicSalary} onChange={handleChange} />
                    <TextField name="employeeCode" label="Employee Code" value={form.employeeCode} onChange={handleChange} />
                    <TextField name="department" label="Department" value={form.department} onChange={handleChange} />
                    <TextField name="designation" label="Designation" value={form.designation} onChange={handleChange} />

                    <FormControl>
                        <InputLabel>Branch</InputLabel>
                        <Select name="branchId" value={form.branchId} onChange={handleChange} label="Branch">
                            {branches.map(b => (
                                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <InputLabel>Role</InputLabel>
                        <Select name="role" value={form.role} onChange={handleChange} label="Role">
                            <MenuItem value="EMPLOYEE">Employee</MenuItem>
                            <MenuItem value="MANAGER">Manager (Cluster)</MenuItem>
                            <MenuItem value="APPROVAL_MANAGER">Approval Manager</MenuItem>
                            <MenuItem value="HR">HR</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl>
                        <InputLabel>Cluster Manager</InputLabel>
                        <Select name="managerId" value={form.managerId} onChange={handleChange} label="Cluster Manager">
                            <MenuItem value="">None</MenuItem>
                            {managers.map(m => (
                                <MenuItem key={m.id} value={m.id}>{m.name} ({m.email})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <InputLabel>Approval Manager (1st Level)</InputLabel>
                        <Select
                            name="approvalManagerId"
                            value={form.approvalManagerId}
                            onChange={handleChange}
                            label="Approval Manager (1st Level)"
                        >
                            <MenuItem value="">None (goes directly to cluster manager)</MenuItem>
                            {approvalManagers.map(am => (
                                <MenuItem key={am.id} value={am.id}>
                                    {am.name} — {am.branchName || 'Any Branch'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {form.role === "MANAGER" && (
                        <FormControl>
                            <InputLabel>Managed Branches</InputLabel>
                            <Select
                                multiple
                                value={form.managedBranches}
                                onChange={(e) => setForm({ ...form, managedBranches: e.target.value })}
                                label="Managed Branches"
                                renderValue={(selected) =>
                                    selected.map(id => branches.find(b => b.id === id)?.name).join(", ")
                                }
                            >
                                {branches.map(branch => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        <Checkbox checked={form.managedBranches.includes(branch.id)} />
                                        <ListItemText primary={branch.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <TextField name="payDay" label="Pay Day" type="number" value={form.payDay} onChange={handleChange} />

                    <FormControl>
                        <InputLabel>Payroll Cycle</InputLabel>
                        <Select name="payrollCycleType" value={form.payrollCycleType} onChange={handleChange} label="Payroll Cycle">
                            <MenuItem value="21_20">21 → 20</MenuItem>
                            <MenuItem value="1_30">1 → End</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl>
                        <InputLabel>End of Month Payroll</InputLabel>
                        <Select
                            value={form.endOfMonthPayroll}
                            onChange={(e) => setForm({ ...form, endOfMonthPayroll: e.target.value })}
                            label="End of Month Payroll"
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>

                </Box>

                <Box sx={{ mt: 2.5, display: "flex", gap: 2 }}>
                    <Button variant="contained" onClick={createEmployee}>
                        {form.id ? "Update" : "Create"}
                    </Button>
                    {form.id && (
                        <Button variant="outlined" onClick={resetForm}>Cancel</Button>
                    )}
                </Box>
            </Paper>

            {/* ── EMPLOYEE TABLE (unchanged) ── */}
            <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                    Employees ({employees.length})
                </Typography>

                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                                <TableCell><b>ID</b></TableCell>
                                <TableCell><b>Code</b></TableCell>
                                <TableCell><b>Name</b></TableCell>
                                <TableCell><b>Email</b></TableCell>
                                <TableCell><b>Branch</b></TableCell>
                                <TableCell><b>Role</b></TableCell>
                                <TableCell><b>Manager</b></TableCell>
                                <TableCell><b>Approval Manager</b></TableCell>
                                <TableCell><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employees.map(e => {
                                const managerObj = e.managerId ? employees.find(m => m.id === e.managerId) : null;
                                return (
                                    <TableRow key={e.id} hover>
                                        <TableCell>{e.id}</TableCell>
                                        <TableCell>{e.employeeCode || "-"}</TableCell>
                                        <TableCell><b>{e.name}</b></TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{e.email}</TableCell>
                                        <TableCell>{e.branchName || "-"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={e.role}
                                                size="small"
                                                color={
                                                    e.role === 'APPROVAL_MANAGER' ? 'secondary' :
                                                    e.role === 'MANAGER' ? 'primary' :
                                                    e.role === 'HR' ? 'info' : 'default'
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{managerObj?.name || e.managerName || "-"}</TableCell>
                                        <TableCell>
                                            {e.approvalManagerName
                                                ? <Chip label={e.approvalManagerName} size="small" color="secondary" variant="outlined" />
                                                : <Typography fontSize={12} color="text.disabled">—</Typography>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                <Button size="small" variant="outlined" onClick={() => handleEdit(e)}>
                                                    Edit
                                                </Button>
                                                <Button size="small" variant="outlined" color="warning" onClick={() => openPwDialog(e)}>
                                                    Change Password
                                                </Button>
                                                <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(e.id, e.name)}>
                                                    Delete
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* ── CHANGE PASSWORD DIALOG (unchanged) ── */}
            <Dialog open={pwDialog} onClose={closePwDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    {pwTarget && (
                        <Typography sx={{ mb: 2, color: "text.secondary" }}>
                            Changing password for: <strong>{pwTarget.name}</strong> ({pwTarget.email})
                        </Typography>
                    )}
                    <TextField
                        fullWidth label="New Password" type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 6 characters"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePwDialog}>Cancel</Button>
                    <Button variant="contained" onClick={submitChangePassword} disabled={pwLoading}>
                        {pwLoading ? "Saving..." : "Change Password"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
